import { useState, useRef, useCallback, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, Upload, X, Check, Trash2, Edit2, Save, GripVertical, Eye, EyeOff, Share2, Pin, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const PIN_COLORS = [
  { name: "Amber", value: "#f59e0b", text: "text-black" },
  { name: "Red", value: "#ef4444", text: "text-white" },
  { name: "Blue", value: "#3b82f6", text: "text-white" },
  { name: "Green", value: "#22c55e", text: "text-black" },
  { name: "Purple", value: "#a855f7", text: "text-white" },
  { name: "Pink", value: "#ec4899", text: "text-white" },
  { name: "Cyan", value: "#06b6d4", text: "text-black" },
  { name: "White", value: "#ffffff", text: "text-black" },
];

// ============ MAP EDITOR (Create / Edit Mode) ============

export function MapEditor({ initialData, onSave, onCancel, gameId, API }) {
  const [title, setTitle] = useState(initialData?.title || "");
  const [imageUrl, setImageUrl] = useState(initialData?.mapImageUrl || "");
  const [pins, setPins] = useState(initialData?.pins || []);
  const [uploading, setUploading] = useState(false);
  const [editingPinId, setEditingPinId] = useState(null);
  const [colorPickerPinId, setColorPickerPinId] = useState(null);
  const imageRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // Get Cloudinary signature
      const sigResp = await axios.get(
        `${API}/cloudinary/signature?folder=gamepop/maps&resource_type=image`
      );
      const sigData = sigResp.data;

      if (!sigData.api_key || !sigData.cloud_name) {
        toast.error("Cloudinary not configured");
        return;
      }

      // Upload directly to Cloudinary
      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", sigData.api_key);
      formData.append("timestamp", sigData.timestamp);
      formData.append("signature", sigData.signature);
      formData.append("folder", sigData.folder);
      formData.append("transformation", sigData.transformation);

      const uploadResp = await fetch(
        `https://api.cloudinary.com/v1_1/${sigData.cloud_name}/image/upload`,
        { method: "POST", body: formData }
      );
      const uploadData = await uploadResp.json();

      if (uploadData.secure_url) {
        setImageUrl(uploadData.secure_url);
        setPins([]); // Reset pins when new image is uploaded
      } else {
        console.error("Upload response:", uploadData);
        toast.error(uploadData.error?.message || "Image upload failed");
      }
    } catch (err) {
      console.error("Upload failed:", err);
      toast.error("Image upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleImageClick = (e) => {
    if (!imageUrl || !imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const newPin = {
      id: `pin_${Date.now()}`,
      x: Math.round(x * 100) / 100,
      y: Math.round(y * 100) / 100,
      label: `Location ${pins.length + 1}`,
      color: "#f59e0b",
      checked: false,
    };
    setPins((prev) => [...prev, newPin]);
    setEditingPinId(newPin.id);
  };

  const updatePinLabel = (pinId, label) => {
    setPins((prev) => prev.map((p) => (p.id === pinId ? { ...p, label } : p)));
  };

  const updatePinColor = (pinId, color) => {
    setPins((prev) => prev.map((p) => (p.id === pinId ? { ...p, color } : p)));
    setColorPickerPinId(null);
  };

  const removePin = (pinId) => {
    setPins((prev) => prev.filter((p) => p.id !== pinId));
    if (editingPinId === pinId) setEditingPinId(null);
  };

  const handleSave = () => {
    if (!title.trim()) return;
    onSave({
      title: title.trim(),
      text: title.trim(),
      noteType: "map",
      mapImageUrl: imageUrl,
      pins,
      isPrivate: false,
    });
  };

  return (
    <div className="space-y-4" data-testid="map-editor">
      {/* Title */}
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Map title (e.g. Collectible Locations)"
        className="bg-white/5 border-white/10 text-white"
        data-testid="map-title-input"
      />

      {/* Image Upload */}
      {!imageUrl ? (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-white/20 rounded-lg p-12 text-center cursor-pointer
                     hover:border-amber-500/40 hover:bg-amber-500/5 transition-all"
          data-testid="map-upload-area"
        >
          <Upload className="w-8 h-8 text-white/40 mx-auto mb-2" />
          <p className="text-white/50 text-sm">
            {uploading ? "Uploading..." : "Click to upload a map image"}
          </p>
          <p className="text-white/30 text-xs mt-1">PNG, JPG up to 10MB</p>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-white/50 text-xs">
              Click on the image to place pins ({pins.length} placed)
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setImageUrl("");
                setPins([]);
              }}
              className="text-red-400 hover:text-red-300 h-6 px-2 text-xs"
            >
              Replace Image
            </Button>
          </div>

          {/* Image with pins */}
          <div className="relative inline-block w-full rounded-lg overflow-hidden border border-white/10">
            <img
              ref={imageRef}
              src={imageUrl}
              alt="Map"
              onClick={handleImageClick}
              className="w-full cursor-crosshair select-none"
              draggable={false}
              data-testid="map-image"
            />
            {pins.map((pin, idx) => (
              <div
                key={pin.id}
                className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
              >
                <div
                  className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center shadow-lg shadow-black/50"
                  style={{ backgroundColor: pin.color || "#f59e0b" }}
                >
                  <span className={`text-[10px] font-bold ${(pin.color === "#ffffff" || pin.color === "#22c55e" || pin.color === "#06b6d4" || pin.color === "#f59e0b" || !pin.color) ? "text-black" : "text-white"}`}>
                    {idx + 1}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />

      {/* Pin List */}
      {pins.length > 0 && (
        <div className="space-y-1.5 max-h-60 overflow-y-auto" data-testid="pin-list">
          <p className="text-white/50 text-xs font-medium uppercase tracking-wider">
            Locations
          </p>
          {pins.map((pin, idx) => (
            <div
              key={pin.id}
              className="flex items-center gap-2 bg-white/5 rounded-md px-2 py-1.5 group"
            >
              {/* Color-clickable pin number */}
              <div className="relative">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 cursor-pointer hover:ring-2 hover:ring-white/30 transition-all"
                  style={{ backgroundColor: pin.color || "#f59e0b" }}
                  onClick={() => setColorPickerPinId(colorPickerPinId === pin.id ? null : pin.id)}
                  title="Click to change color"
                  data-testid={`pin-color-${idx}`}
                >
                  <span className={`text-[10px] font-bold ${(pin.color === "#ffffff" || pin.color === "#22c55e" || pin.color === "#06b6d4" || pin.color === "#f59e0b" || !pin.color) ? "text-black" : "text-white"}`}>
                    {idx + 1}
                  </span>
                </div>
                {/* Color picker dropdown */}
                {colorPickerPinId === pin.id && (
                  <div className="absolute left-0 top-7 z-50 bg-[#1a1a24] border border-white/10 rounded-lg p-1.5 flex gap-1 shadow-xl" data-testid={`pin-color-picker-${idx}`}>
                    {PIN_COLORS.map((c) => (
                      <div
                        key={c.value}
                        className={`w-5 h-5 rounded-full cursor-pointer hover:scale-125 transition-transform ${pin.color === c.value ? "ring-2 ring-white" : ""}`}
                        style={{ backgroundColor: c.value }}
                        onClick={() => updatePinColor(pin.id, c.value)}
                        title={c.name}
                      />
                    ))}
                  </div>
                )}
              </div>
              {editingPinId === pin.id ? (
                <Input
                  autoFocus
                  value={pin.label}
                  onChange={(e) => updatePinLabel(pin.id, e.target.value)}
                  onFocus={(e) => e.target.select()}
                  onBlur={() => setEditingPinId(null)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && setEditingPinId(null)
                  }
                  className="h-6 text-xs bg-white/5 border-amber-500/30 text-white flex-1"
                  data-testid={`pin-label-input-${idx}`}
                />
              ) : (
                <span
                  onClick={() => setEditingPinId(pin.id)}
                  className="text-white/80 text-xs cursor-pointer hover:text-amber-300 flex-1 truncate"
                  data-testid={`pin-label-${idx}`}
                >
                  {pin.label}
                </span>
              )}
              <button
                onClick={() => removePin(pin.id)}
                className="text-white/20 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                data-testid={`pin-remove-${idx}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          className="text-white/60"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!title.trim() || !imageUrl}
          className="bg-amber-600 hover:bg-amber-500 text-black font-medium"
          data-testid="map-save-btn"
        >
          <Save className="w-3.5 h-3.5 mr-1.5" />
          {initialData ? "Update Map" : "Save Map"}
        </Button>
      </div>
    </div>
  );
}

// ============ MAP DISPLAY (View / Checklist Mode) ============

export function MapDisplay({ note, onTogglePin, onEdit, onDelete, onSubmitToCommunity, onPinToPlanner, headerExtras = null }) {
  const [expanded, setExpanded] = useState(false);
  const [showChecked, setShowChecked] = useState(true);
  const imageRef = useRef(null);

  const pins = note.pins || [];
  const checkedCount = pins.filter((p) => p.checked).length;
  const totalCount = pins.length;
  const progress = totalCount > 0 ? (checkedCount / totalCount) * 100 : 0;

  const handlePinToggle = (pinId) => {
    const pin = pins.find((p) => p.id === pinId);
    if (pin) {
      onTogglePin(note.id, pinId, !pin.checked);
    }
  };

  return (
    <Card
      className="bg-[#121212] border-white/5 hover:border-amber-500/30 transition-colors overflow-hidden"
      data-testid={`map-card-${note.id}`}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 px-3 py-2 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <MapPin className="w-4 h-4 text-amber-400 shrink-0" />
        <ChevronRight
          className={`w-3.5 h-3.5 text-white/40 transition-transform shrink-0 ${expanded ? "rotate-90" : ""}`}
        />
        <span className="text-white/90 text-sm font-medium flex-1 truncate">
          {note.title || "Untitled Map"}
        </span>

        {/* Progress */}
        <span className="text-white/40 text-xs tabular-nums">
          {checkedCount}/{totalCount}
        </span>

        {/* Progress bar mini */}
        <div className="w-12 h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-amber-500 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 ml-1" onClick={(e) => e.stopPropagation()}>
          {onPinToPlanner && (
            <button
              onClick={() => onPinToPlanner?.(note)}
              className={`transition-colors p-0.5 ${note.pinnedToPlanner ? "text-cyan-400" : "text-white/50 hover:text-cyan-400"}`}
              title={note.pinnedToPlanner ? "Unpin from planner" : "Pin to planner"}
              data-testid={`map-pin-planner-${note.id}`}
            >
              <Pin className="w-3.5 h-3.5" />
            </button>
          )}
          {onSubmitToCommunity && (
            <button
              onClick={() => onSubmitToCommunity?.(note)}
              className="text-white/50 hover:text-cyan-400 transition-colors p-0.5"
              title="Submit to Community"
              data-testid={`map-share-${note.id}`}
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={() => onEdit?.(note)}
            className="text-white/50 hover:text-amber-400 transition-colors p-0.5"
            title="Edit map"
            data-testid={`map-edit-${note.id}`}
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          {headerExtras}
          <button
            onClick={() => onDelete?.(note.id)}
            className="text-white/50 hover:text-red-400 transition-colors p-0.5"
            title="Delete map"
            data-testid={`map-delete-${note.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <CardContent className="p-0">
          <div className="flex flex-col lg:flex-row">
            {/* Map Image */}
            <div className="relative flex-1 min-w-0">
              <img
                ref={imageRef}
                src={note.mapImageUrl}
                alt={note.title}
                className="w-full select-none"
                draggable={false}
              />
              {pins.map((pin, idx) => (
                <div
                  key={pin.id}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 transition-all duration-300 cursor-pointer
                    ${pin.checked ? "opacity-30 scale-75" : "opacity-100 scale-100"}
                    ${!showChecked && pin.checked ? "hidden" : ""}
                  `}
                  style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
                  onClick={() => handlePinToggle(pin.id)}
                  title={pin.label}
                >
                  <div
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shadow-lg shadow-black/50 transition-colors
                      ${pin.checked
                        ? "bg-white/20 border-white/30"
                        : "border-white"
                      }
                    `}
                    style={!pin.checked ? { backgroundColor: pin.color || "#f59e0b" } : undefined}
                  >
                    {pin.checked ? (
                      <Check className="w-3 h-3 text-white/50" />
                    ) : (
                      <span className={`text-[10px] font-bold ${(pin.color === "#ffffff" || pin.color === "#22c55e" || pin.color === "#06b6d4" || pin.color === "#f59e0b" || !pin.color) ? "text-black" : "text-white"}`}>
                        {idx + 1}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Sidebar Pin List */}
            <div className="lg:w-56 border-t lg:border-t-0 lg:border-l border-white/5 bg-black/20">
              <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
                <span className="text-white/40 text-xs font-medium uppercase tracking-wider">
                  Locations
                </span>
                <button
                  onClick={() => setShowChecked(!showChecked)}
                  className="text-white/30 hover:text-white/60 transition-colors"
                  title={showChecked ? "Hide completed" : "Show completed"}
                >
                  {showChecked ? (
                    <Eye className="w-3.5 h-3.5" />
                  ) : (
                    <EyeOff className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {pins.map((pin, idx) => {
                  if (!showChecked && pin.checked) return null;
                  return (
                    <button
                      key={pin.id}
                      onClick={() => handlePinToggle(pin.id)}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/5 transition-colors
                        ${pin.checked ? "opacity-40" : ""}
                      `}
                      data-testid={`map-pin-toggle-${idx}`}
                    >
                      <div
                        className={`w-4 h-4 rounded-full shrink-0 flex items-center justify-center text-[9px] font-bold
                          ${pin.checked
                            ? "bg-white/10 text-white/40 line-through"
                            : ""
                          }
                        `}
                        style={!pin.checked ? { backgroundColor: pin.color || "#f59e0b", color: (pin.color === "#ffffff" || pin.color === "#22c55e" || pin.color === "#06b6d4" || pin.color === "#f59e0b" || !pin.color) ? "#000" : "#fff" } : undefined}
                      >
                        {pin.checked ? (
                          <Check className="w-2.5 h-2.5" />
                        ) : (
                          idx + 1
                        )}
                      </div>
                      <span
                        className={`text-xs truncate ${
                          pin.checked
                            ? "text-white/30 line-through"
                            : "text-white/70"
                        }`}
                      >
                        {pin.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
